// Alerts (UI design §3.1): open alerts of the sites the user can see, acknowledge; managers also resolve.
// Live: realtime on alerts.
import {
  Button,
  Chip,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { canManage, useSession } from '../auth/session';
import { Empty, Loading, LoadError, Page } from '../components/Page';
import { type AlertRow, useAcknowledge, useAlerts } from '../data/alerts';
import { useLiveInvalidate } from '../data/realtime';
import { dateTime, relativeTime } from '../lib/time';

const SEVERITY_COLOR = { info: 'default', warning: 'warning', critical: 'error' } as const;

function subject(a: AlertRow) {
  if (a.assets) return { label: a.assets.asset_code, to: `/assets/${a.asset_id}` };
  if (a.transfers)
    return { label: a.transfers.ref, to: a.site_id ? `/sites/${a.site_id}` : undefined };
  if (a.gateways) return { label: a.gateways.display_name ?? a.gateways.identifier, to: undefined };
  return { label: '—', to: undefined };
}

export function AlertsPage() {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const [all, setAll] = useState(false);
  const alerts = useAlerts(all);
  const ack = useAcknowledge();
  useLiveInvalidate('alerts-page', ['alerts'], [['alerts']]);
  const manager = canManage(me);

  return (
    <Page
      title={t('alerts.title')}
      actions={
        <FormControlLabel
          control={<Switch checked={all} onChange={(e) => setAll(e.target.checked)} />}
          label={t('alerts.show_resolved')}
        />
      }
    >
      {alerts.isLoading ? <Loading /> : null}
      {alerts.error ? (
        <LoadError error={alerts.error} onRetry={() => void alerts.refetch()} />
      ) : null}
      {alerts.data && !alerts.data.length ? <Empty>{t('alerts.none')}</Empty> : null}
      {alerts.data?.length ? (
        <Table size="small" aria-label={t('alerts.title')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('alerts.col_severity')}</TableCell>
              <TableCell>{t('alerts.col_alert')}</TableCell>
              <TableCell>{t('alerts.col_subject')}</TableCell>
              <TableCell>{t('alerts.col_site')}</TableCell>
              <TableCell>{t('alerts.col_raised')}</TableCell>
              <TableCell>{t('alerts.col_status')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.data.map((a) => {
              const s = subject(a);
              return (
                <TableRow key={a.id} hover data-testid={`alert-${a.rule}`}>
                  <TableCell>
                    <Chip
                      size="small"
                      color={SEVERITY_COLOR[a.severity]}
                      label={t(`alerts.severity_${a.severity}`)}
                    />
                  </TableCell>
                  <TableCell>{t(`alert.rule.${a.rule}`, { defaultValue: a.message })}</TableCell>
                  <TableCell>
                    {s.to ? (
                      <Link component={RouterLink} to={s.to}>
                        {s.label}
                      </Link>
                    ) : (
                      s.label
                    )}
                  </TableCell>
                  <TableCell>{a.sites?.name ?? '—'}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        dateTime(
                          a.created_at,
                          i18n.language,
                          me?.company?.timezone ?? 'Asia/Kuala_Lumpur',
                        ) ?? ''
                      }
                    >
                      <span>{relativeTime(a.created_at, i18n.language)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {a.resolved_at
                      ? t('alerts.resolved')
                      : a.acknowledged_at
                        ? t('alerts.acknowledged')
                        : t('alerts.open')}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {!a.acknowledged_at && !a.resolved_at ? (
                        <Button
                          size="small"
                          disabled={ack.isPending}
                          onClick={() => ack.mutate({ id: a.id, resolve: false })}
                        >
                          {t('alerts.acknowledge')}
                        </Button>
                      ) : null}
                      {manager && !a.resolved_at ? (
                        <Button
                          size="small"
                          color="secondary"
                          disabled={ack.isPending}
                          onClick={() => ack.mutate({ id: a.id, resolve: true })}
                        >
                          {t('alerts.resolve')}
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}
    </Page>
  );
}
